import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuTriangleAlert } from 'react-icons/lu';
import {
  fetchGroupRows, fetchMovementById, getReferenceData, groupMovements,
  searchMovements, updateMovement,
} from '../../api/endpointsBackend';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { movementAlertReasons, directionEvidence } from '../../utils/movementAlertReasons';
import { reviewFilters } from '../../utils/movementAlerts';
import { todayLocalISO } from '../../utils/localDate';
import { formatRowAmount } from '../../utils/formatters';
import { getAccountDisplay, getPrimaryText } from '../../utils/movementDisplay';
import usePhoneHomeCurrency from '../../hooks/usePhoneHomeCurrency';
import { usePhoneShell } from './PhoneShellContext';
import { CategoryDisc, shortDayLabel } from './PhonePickers';
import { loadTopCategoryIds } from './quickAddLists';

// Review's shared parts: which fix a movement gets, the reason it shows, the
// suggested categories, "Later", and the queue both Review views work from.

const ROW_LIMIT = 100;
const SNOOZE_MS = 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// Reasons and fixes
// ---------------------------------------------------------------------------

const isIncomeOrExpense = (m) => m?.type === 'Income' || m?.type === 'Expenses';

/** Only the category is missing: the one gap a tile can close. */
const missesOnlyCategory = (m) => isIncomeOrExpense(m) && m.category_id == null && m.account_id != null;

/**
 * What still needs the user on a movement, as sentences: the reasons of the
 * alerts the user's preferences leave on, minus "waiting for the bank",
 * which has nothing to fix. A movement missing only its category says so in
 * so many words.
 */
export function fixableReasons(movement, { accounts, prefs, t }) {
  return movementAlertReasons(movement, { accounts, t, prefs })
    .filter((r) => r.tone === 'alert')
    .map((r) => (r.flag === 'is_incomplete' && missesOnlyCategory(movement)
      ? { ...r, text: t('review.reasons.noCategory') }
      : r));
}

/**
 * The one-tap fix a movement's first reason allows:
 * - `category`: tiles of its type;
 * - `switch`: the rules say money went the other way, so the type flips
 *   (Income or Expenses only), with a category of the new type;
 * - `move`: its bank row sits on exactly one other account;
 * - `open`: anything else, including every Transfer. A PUT without `type`
 *   skips type validation and would write an `account_id` onto a transfer,
 *   and a type switch nulls its legs.
 */
export function reviewFix(movement, reasons, accounts = []) {
  const flag = reasons[0]?.flag;
  if (!isIncomeOrExpense(movement) || !flag) return { kind: 'open' };
  if (flag === 'is_incomplete' && missesOnlyCategory(movement)) return { kind: 'category' };
  if (flag === 'needs_source_rule_review' && directionEvidence(movement, movement, accounts)) {
    return { kind: 'switch', to: movement.type === 'Income' ? 'Expenses' : 'Income' };
  }
  if (flag === 'has_account_mismatch') {
    const others = [...new Set((movement.statement_links || [])
      .map((link) => link.account_id)
      .filter((id) => id != null && id !== movement.account_id))];
    const account = others.length === 1 ? accounts.find((a) => a.id === others[0]) : null;
    if (account) return { kind: 'move', account };
  }
  return { kind: 'open' };
}

/**
 * "Thu, Sep 24", with the year when it is not this one: Review is all-time,
 * and a date from another year would otherwise read as this year's.
 */
export function reviewDateLabel(iso, t) {
  if (!iso) return '';
  const label = shortDayLabel(iso, t);
  const year = Number(String(iso).slice(0, 4));
  return year === new Date().getFullYear() ? label : `${label}, ${year}`;
}

/** The reason line: the alert colour, the warning mark, the sentence. */
export function ReasonLine({ text, fontSize = '14px' }) {
  return (
    <Flex gap="7px" fontSize={fontSize} lineHeight="1.45" color="phone.alert" align="flex-start" textAlign="left">
      <Box as={LuTriangleAlert} boxSize="16px" flexShrink={0} mt="2px" aria-hidden="true" />
      <Box as="span">{text}</Box>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Later (snooze), per device and workspace
// ---------------------------------------------------------------------------

const snoozeKey = (workspaceId) => `mk.reviewSnooze.${workspaceId}`;

function readSnoozes(workspaceId) {
  try {
    const raw = JSON.parse(localStorage.getItem(snoozeKey(workspaceId)) || '{}');
    const now = Date.now();
    return Object.fromEntries(Object.entries(raw).filter(([, until]) => until > now));
  } catch {
    return {};
  }
}

function writeSnoozes(workspaceId, snoozes) {
  try {
    localStorage.setItem(snoozeKey(workspaceId), JSON.stringify(snoozes));
  } catch {
    /* private mode: Later lasts as long as the page */
  }
}

// "N fixed today", per device and workspace.
const fixedKey = (workspaceId) => `mk.reviewFixed.${workspaceId}`;

export function readFixedToday(workspaceId) {
  try {
    const raw = JSON.parse(localStorage.getItem(fixedKey(workspaceId)) || '{}');
    return raw.day === todayLocalISO() ? raw.count || 0 : 0;
  } catch {
    return 0;
  }
}

function addFixedToday(workspaceId, delta) {
  const count = Math.max(0, readFixedToday(workspaceId) + delta);
  try {
    localStorage.setItem(fixedKey(workspaceId), JSON.stringify({ day: todayLocalISO(), count }));
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Suggested categories
// ---------------------------------------------------------------------------

const countByCategory = (rows) => {
  const counts = new Map();
  (rows || []).forEach((r) => {
    if (r.category_id == null) return;
    counts.set(r.category_id, (counts.get(r.category_id) || 0) + (r.num_movements || 0));
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
};

// The type's most used categories of the last 90 days: the same list, and the
// same session cache, as quick add's tiles and the movement sheet's picker.
const mostUsedIds = (type) => loadTopCategoryIds(type);

const historyKey = (type, note) => `${type}|${note}`;

/**
 * What the user chose before for each name in `movements` that misses only its
 * category: `Map<"type|note", categoryId[]>`, most used first. One request per
 * type for the whole queue, rather than one per card.
 */
async function noteHistoryOf(movements) {
  const namesByType = new Map();
  movements.forEach((m) => {
    if (!missesOnlyCategory(m) || !m.note) return;
    if (!namesByType.has(m.type)) namesByType.set(m.type, new Set());
    namesByType.get(m.type).add(m.note);
  });
  const history = new Map();
  await Promise.all([...namesByType].map(([type, names]) => groupMovements(
    [
      { entity: 'Movement', column: 'note', operator: 'in', values: [...names] },
      { entity: 'Movement', column: 'type', operator: 'eq', values: [type] },
    ],
    ['currency', 'note', 'category_id'],
  ).then((rows) => {
    const byNote = new Map();
    (rows || []).forEach((r) => {
      if (!byNote.has(r.note)) byNote.set(r.note, []);
      byNote.get(r.note).push(r);
    });
    byNote.forEach((list, note) => history.set(historyKey(type, note), countByCategory(list)));
  }).catch(() => {})));
  return history;
}

/**
 * Categories to offer a movement with none: what the user chose before for
 * this name, filled up with the type's most used. Only categories of the
 * movement's type, because a PUT of `category_id` alone is not type-checked.
 * `noteHistory` is the queue's (`useReviewQueue().noteHistory`); without it
 * the movement's own history is asked for.
 */
export function useCategorySuggestions(movement, categories, count, noteHistory = null) {
  const { currentId } = useWorkspace();
  const [ids, setIds] = useState([]);
  // The name is the note; a fallback title (the description) is not searchable.
  const name = movement?.note || null;
  const type = movement?.type;

  useEffect(() => {
    if (!movement || !isIncomeOrExpense(movement) || movement.category_id != null) return undefined;
    let active = true;
    let history = Promise.resolve([]);
    if (name && noteHistory) {
      history = Promise.resolve(noteHistory.get(historyKey(type, name)) || []);
    } else if (name) {
      history = groupMovements(
        [
          { entity: 'Movement', column: 'note', operator: 'eq', values: [name] },
          { entity: 'Movement', column: 'type', operator: 'eq', values: [type] },
        ],
        ['currency', 'category_id'],
      ).then(countByCategory).catch(() => []);
    }
    Promise.all([history, mostUsedIds(type).catch(() => [])]).then(([own, popular]) => {
      if (active) setIds([...new Set([...own, ...popular])]);
    });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movement?.id, movement?.category_id, name, type, currentId, noteHistory]);

  return useMemo(() => {
    const ofType = (categories || []).filter((c) => c.type === type);
    const byId = new Map(ofType.map((c) => [c.id, c]));
    const picked = ids.map((id) => byId.get(id)).filter(Boolean);
    return { tiles: picked.slice(0, count), recentIds: picked.map((c) => c.id) };
  }, [ids, categories, type, count]);
}

// ---------------------------------------------------------------------------
// Undo toast
// ---------------------------------------------------------------------------

function UndoToast({ title, undoLabel, onUndo, onClose }) {
  return (
    <Flex
      align="center"
      gap="8px"
      pl="16px"
      pr="4px"
      minH="52px"
      borderRadius="14px"
      bg="redesign.bg.popup"
      border="1px solid"
      borderColor="redesign.border"
      boxShadow="lg"
      role="status"
    >
      <Text flex="1" minW={0} fontSize="15px" color="redesign.heading" noOfLines={2}>{title}</Text>
      <Button
        variant="ghost"
        color="phone.link"
        fontSize="16px"
        fontWeight="600"
        minH="44px"
        px="12px"
        onClick={() => { onClose(); onUndo(); }}
      >
        {undoLabel}
      </Button>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// The queue
// ---------------------------------------------------------------------------

/** The list with `movement` in its place, newest date first. */
function placeInList(list, movement) {
  return [...list.filter((m) => m.id !== movement.id), movement]
    .sort((a, b) => String(b.date).localeCompare(String(a.date)) || b.id - a.id);
}

const createdToday = (m, today) => m.created_at && todayLocalISO(new Date(m.created_at)) === today;

/**
 * Everything Review shows, loaded while it is open: the movements to fix,
 * the ones only waiting for the bank, what arrived today, and the accounts
 * and categories the reasons and fixes need. Also the fix, Later and Undo
 * actions both Review views use.
 */
export function useReviewQueue(isOpen) {
  const { t } = useTranslation('movements');
  const toast = useToast();
  const { alerts: prefs } = usePreferences();
  const { currentId, can } = useWorkspace();
  const { refreshSignal, bumpRefresh } = usePhoneShell();
  const canFix = can('movement.update.own');

  const [state, setState] = useState({
    loading: true, fixable: [], waiting: [], arrived: [], accounts: [], categories: [], currencies: [], noteHistory: null,
  });
  const [snoozes, setSnoozes] = useState(() => readSnoozes(currentId));
  const loadedFor = useRef(null);
  // Each load takes a number; a load that a newer one has overtaken is dropped.
  const loadSeq = useRef(0);
  // Fixes and undos made here, each stamped with the newest load that had
  // started when it was made. A load that started before an edit can come back
  // without it, so the edit is replayed over that load's list; a load that
  // started after it already has it, and the edit is forgotten.
  const edits = useRef([]);

  useEffect(() => { setSnoozes(readSnoozes(currentId)); }, [currentId]);

  const load = useCallback(async () => {
    loadSeq.current += 1;
    const seq = loadSeq.current;
    const current = () => seq === loadSeq.current;
    const filters = reviewFilters(prefs);
    const rows = (list) => (list
      ? fetchGroupRows({ filters: list, orderBy: 'date_desc', limit: ROW_LIMIT }).then((r) => r.items || [])
      : Promise.resolve([]));
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const today = todayLocalISO();

    // Waiting and "Arrived today" load on their own: the cards to fix are
    // what Review is for, and must not wait for the slower search.
    rows(filters.waiting)
      .then((waiting) => { if (current()) setState((s) => ({ ...s, waiting })); })
      .catch(() => {});
    // Created at is a timestamp; ask from yesterday and keep today's, local.
    searchMovements(
      [
        { entity: 'Movement', column: 'created_at', operator: 'gte', values: [todayLocalISO(yesterday)] },
        { entity: 'Movement', column: 'is_ignored', operator: 'eq', values: [false] },
      ],
      [{ entity: 'Movement', column: 'created_at', sort: 'desc' }],
      null, null, 50, 0,
    )
      .then((r) => {
        if (!current()) return;
        const arrived = (r.items || []).filter((m) => createdToday(m, today))
          .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
        setState((s) => ({ ...s, arrived }));
      })
      .catch(() => {});

    const [[fixable, noteHistory], ref] = await Promise.all([
      rows(filters.fixable).then(async (list) => [list, await noteHistoryOf(list)]),
      getReferenceData().catch(() => ({ accounts: [], categories: [], currencies: [] })),
    ]);
    if (!current()) return;
    edits.current = edits.current.filter((e) => e.seq >= seq);
    setState((s) => ({
      ...s,
      loading: false,
      fixable: edits.current.reduce((list, e) => e.apply(list), fixable),
      noteHistory,
      accounts: ref.accounts,
      categories: ref.categories,
      currencies: (ref.currencies || []).map((c) => c.name),
    }));
  }, [prefs]);

  useEffect(() => {
    if (!isOpen) return;
    const key = `${currentId}|${refreshSignal}|${JSON.stringify(prefs)}`;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    load().catch(() => setState((s) => ({ ...s, loading: false })));
  }, [isOpen, currentId, refreshSignal, prefs, load]);

  useEffect(() => {
    // Reopening asks again: the inbox may have changed while it was closed.
    if (!isOpen) loadedFor.current = null;
  }, [isOpen]);

  const reasonsOf = useCallback(
    (m) => fixableReasons(m, { accounts: state.accounts, prefs, t }),
    [state.accounts, prefs, t],
  );

  /** Apply an edit to the list now, and keep it for any load already in flight. */
  const edit = useCallback((apply) => {
    edits.current = [...edits.current, { seq: loadSeq.current, apply }];
    setState((s) => ({ ...s, fixable: apply(s.fixable) }));
  }, []);

  /** Replace a movement in the list with its fresh copy, or drop it when nothing is left to fix. */
  const settle = useCallback((fresh) => {
    const resolved = reasonsOf(fresh).length === 0;
    edit((list) => (resolved
      ? list.filter((m) => m.id !== fresh.id)
      : placeInList(list, fresh)));
    return resolved;
  }, [reasonsOf, edit]);

  /** Put a movement back as it was before a fix. */
  const restore = useCallback((before) => {
    edit((list) => placeInList(list, before));
  }, [edit]);

  /**
   * Send one fix. Resolves to `{ resolved, undo }`, where `undo()` sends the
   * previous values back; or null when the save failed. A toast offers Undo
   * unless `quiet` (the one-by-one view has its own Undo).
   */
  const applyFix = useCallback(async (movement, payload, message, { quiet = false } = {}) => {
    const previous = Object.fromEntries(Object.keys(payload).map((k) => [k, movement[k] ?? null]));
    try {
      await updateMovement(movement.id, payload);
    } catch {
      toast({ description: t('review.toasts.failed'), status: 'error', duration: 3000, isClosable: true });
      return null;
    }
    const fresh = await fetchMovementById(movement.id).catch(() => ({ ...movement, ...payload }));
    const resolved = settle(fresh);
    if (resolved) addFixedToday(currentId, 1);
    bumpRefresh();

    const undo = async () => {
      try {
        await updateMovement(movement.id, previous);
      } catch {
        toast({ description: t('review.toasts.failed'), status: 'error', duration: 3000, isClosable: true });
        return false;
      }
      if (resolved) addFixedToday(currentId, -1);
      const back = await fetchMovementById(movement.id).catch(() => movement);
      restore(back);
      bumpRefresh();
      return true;
    };

    if (!quiet) {
      toast({
        duration: 5000,
        render: ({ onClose }) => (
          <UndoToast title={message} undoLabel={t('review.undo')} onUndo={undo} onClose={onClose} />
        ),
      });
    }
    return { resolved, undo };
  }, [settle, restore, bumpRefresh, toast, t, currentId]);

  const snooze = useCallback((id) => {
    setSnoozes((prev) => {
      const next = { ...readSnoozes(currentId), ...prev, [id]: Date.now() + SNOOZE_MS };
      writeSnoozes(currentId, next);
      return next;
    });
  }, [currentId]);

  const unsnooze = useCallback((id) => {
    setSnoozes((prev) => {
      const next = { ...prev };
      delete next[id];
      writeSnoozes(currentId, next);
      return next;
    });
  }, [currentId]);

  // The currency amounts drop their code in, as on the movement rows.
  const home = usePhoneHomeCurrency();

  const now = Date.now();
  const active = state.fixable.filter((m) => !(snoozes[m.id] > now));
  const later = state.fixable.filter((m) => snoozes[m.id] > now);

  return {
    ...state,
    active,
    later,
    home,
    prefs,
    canFix,
    reasonsOf,
    applyFix,
    snooze,
    unsnooze,
    reload: load,
  };
}

// ---------------------------------------------------------------------------
// A row only waiting for the bank
// ---------------------------------------------------------------------------

/**
 * A movement only waiting for its bank row, muted: nothing to fix, so no
 * alert mark, no New pill and no orange. "⏳ <account> · <date>" under it.
 */
export function WaitingRow({ movement, home, onClick }) {
  const { t } = useTranslation('movements');
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      w="100%"
      display="grid"
      gridTemplateColumns="40px minmax(0, 1fr)"
      columnGap="12px"
      alignItems="center"
      minH="64px"
      px="16px"
      py="9px"
      textAlign="left"
      _active={{ bg: 'redesign.bg.hover' }}
    >
      <ReviewDisc movement={movement} flag={false} />
      <Box minW={0}>
        <Flex justify="space-between" gap="10px" align="baseline">
          <Text textStyle="phoneRowName" color="redesign.text" isTruncated>{getPrimaryText(movement)}</Text>
          <Text textStyle="phoneRowAmount" color="redesign.text" whiteSpace="nowrap">{formatRowAmount(movement, home).text}</Text>
        </Flex>
        <Flex textStyle="phoneMeta" color="phone.muted" minW={0} mt="1px">
          <Box as="span" flexShrink={0} mr="4px" aria-hidden="true" sx={{ filter: 'grayscale(1)', opacity: 0.75 }}>⏳</Box>
          <Box as="span" minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{getAccountDisplay(movement)}</Box>
          <Box as="span" flexShrink={0} whiteSpace="pre">{` · ${reviewDateLabel(movement.date, t)}`}</Box>
        </Flex>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// The disc
// ---------------------------------------------------------------------------

/** The category's disc (or "?" when it has none), with the alert mark. */
export function ReviewDisc({ movement, size = 40, flag = true }) {
  const category = movement.type === 'Transfer' ? null : movement.category;
  return (
    <Box position="relative" flexShrink={0} w={`${size}px`} h={`${size}px`}>
      {category ? (
        <CategoryDisc category={category} size={size} />
      ) : (
        <Flex
          w={`${size}px`}
          h={`${size}px`}
          borderRadius="full"
          align="center"
          justify="center"
          border="1.5px dashed"
          borderColor="phone.muted"
          color="phone.muted"
          fontSize={`${Math.round(size * 0.42)}px`}
          fontWeight="600"
        >
          {movement.type === 'Transfer' ? '⇄' : '?'}
        </Flex>
      )}
      {flag && (
        <Flex
          position="absolute"
          right="-3px"
          bottom="-3px"
          w="16px"
          h="16px"
          borderRadius="full"
          border="2px solid"
          borderColor="redesign.bg.card"
          bg="phone.badgeBg"
          color="phone.badgeFg"
          align="center"
          justify="center"
          fontSize="9px"
          fontWeight="800"
          aria-hidden="true"
        >
          !
        </Flex>
      )}
    </Box>
  );
}
